import { ThreadStateEnum, UserModel } from '@anecdotes/commenting';
import { Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { FocusingService } from '../focusing/focusing.service';
import { combineLatest, firstValueFrom, from, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
  CommentEntityNameEnum,
  CommentFilterOptionDefinition,
  CommentingConfig,
  CommentingResourceModel,
  CommentSortOptionDefinition,
  ThreadSort,
  ThreadViewModel,
} from '../../models';
import {
  AddNewResourcesAction,
  ChangeSortingOptionAction,
  CloseAction,
  CloseEditAction,
  CloseReplyToThreadAction,
  CloseSingleThreadDisplayAction,
  CloseThreadCreationAction,
  CreateThreadForResourceAction,
  DestroyAction,
  DisplayAllThreadsAction,
  DisplaySingleThreadAction,
  EditCommentAction,
  EditThreadAction,
  FocusResourceAction,
  InitializeCommentingPanelAction,
  OpenAction,
  ReplyToThreadAction,
  SetFilterOptionAction,
} from '../../store';
import {
  featureSelector,
  selectDisplayedSingleThreadResourceId,
  selectDisplayedThreadViewModels,
  selectFilterOptions,
  selectHasUniqueSingleThreadDesign,
  selectIsInitialized,
  selectIsOpened,
  selectResourceType,
  selectSelectedFilterOption,
  selectSortOptions,
  selectSelectedSortOption,
  selectThreadViewModels,
  selectAreThreadsLoaded,
  selectUsersMap,
  selectShouldDisplayBackButton
} from '../../store/selectors';
import { createComposedResourceId } from '../../utils';
import { CommentingUserEventsService } from '../commenting-user-events/commenting-user-events.service';
import { Router } from '@angular/router';
import { selectedSingleThreadControlNameQueryParam } from '../../constants';

@Injectable()
export class CommentPanelManagerService {
  private threadViewModels$: Observable<ThreadViewModel[]>;

  constructor(
    private store: Store,
    private focusingService: FocusingService,
    private commentingUserEventsService: CommentingUserEventsService,
    private router: Router
  ) {
    this.threadViewModels$ = this.getThreadViewModelsStream();
  }

  isPanelOpened(): Observable<boolean> {
    return this.store.select(selectIsOpened);
  }

  isInitialized(): Observable<boolean> {
    return this.store.select(selectIsInitialized);
  }

  getUsersMap(): Observable<Map<string, UserModel>> {
    return this.store.select(selectUsersMap);
  }

  areThreadsLoaded(): Observable<boolean> {
    return this.store.select(selectAreThreadsLoaded);
  }

  getCommentingResources(): Observable<ThreadViewModel[]> {
    return this.threadViewModels$;
  }

  getSelectedFilterOption(): Observable<CommentFilterOptionDefinition> {
    return this.store.select(selectSelectedFilterOption);
  }

  getFilterOptions(): Observable<CommentFilterOptionDefinition[]> {
    return this.store.select(selectFilterOptions);
  }

  getSortOptions(): Observable<CommentSortOptionDefinition[]> {
    return this.store.select(selectSortOptions);
  }

  getThreadsAndRepliesCountForResource(resourceType: string, resourceId: string): Observable<number> {
    return this.getCommentingResources().pipe(
      map(
        (threads) =>
          threads.filter(
            (thread) =>
              thread.resourceType === resourceType && thread.resourceId === resourceId && thread.threadState === ThreadStateEnum.Active
          )?.length
      )
    );
  }

  getSelectedSortOption(): Observable<ThreadSort> {
    return this.store.select(selectSelectedSortOption);
  }

  getDisplayedCommentingResources(): Observable<ThreadViewModel[]> {
    return this.store.select(selectDisplayedThreadViewModels);
  }

  getDisplayedSingleThreadResourceId(): Observable<string> {
    return this.store.select(selectDisplayedSingleThreadResourceId);
  }

  hasUniqueSingleThreadDesign(): Observable<boolean> {
    return this.store.select(selectHasUniqueSingleThreadDesign);
  }

  shouldDisplayBackButton(): Observable<boolean> {
    return this.store.select(selectShouldDisplayBackButton);
  }

  getCommentsCount(): Observable<number> {
    return this.getCommentingResources().pipe(
      map((resources) => resources.filter((res) => res.threadState === ThreadStateEnum.Active).length),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getFocusedResource(): Observable<CommentingResourceModel> {
    return this.focusingService.getFocusingStreamForResource(CommentEntityNameEnum.Resource).pipe(
      tap(() => this.focusingService.finishFocusing(CommentEntityNameEnum.Resource)),
      map(async (focusedComposedResourceId) => {
        const resources = await firstValueFrom(this.getCommentingResources());

        return resources.find(
          (res) => createComposedResourceId(res.resourceType, res.resourceId) === focusedComposedResourceId
        );
      }),
      switchMap((promise) => from(promise)),
      filter(Boolean),
    );
  }

  isResourceFocused(resourceType: string, resourceId: string): Observable<boolean> {
    return combineLatest([
      this.store.select(createSelector(featureSelector, (state) => state.focusedResource)),
      this.store.select(createSelector(featureSelector, (state) => state.threadCreationFor)),
    ]).pipe(
      map(([focusedResource, threadCreationFor]) => {
        return (
          (focusedResource &&
            focusedResource.resourceType === resourceType &&
            focusedResource.resourceId === resourceId) ||
          (threadCreationFor &&
            threadCreationFor.resourceType === resourceType &&
            threadCreationFor.resourceId === resourceId)
        );
      })
    );
  }

  getResourceToCreateThreadFor(): Observable<CommentingResourceModel> {
    return this.store.select(createSelector(featureSelector, (state) => state.threadCreationFor));
  }

  getFocusedResourceId(): Observable<string> {
    return this.store.select(createSelector(featureSelector, (state) => state.focusedResource?.resourceId));
  }

  getFocusedResourceType(): Observable<string> {
    return this.store.select(createSelector(featureSelector, (state) => state.focusedResource?.resourceType));
  }

  getEditingThreadId(): Observable<string> {
    return this.store.select(createSelector(featureSelector, (state) => state.editingThreadId));
  }

  getEditingCommentId(): Observable<string> {
    return this.store.select(createSelector(featureSelector, (state) => state.editingCommentId));
  }

  getReplyingThreadId(): Observable<string> {
    return this.store.select(createSelector(featureSelector, (state) => state.replyingThreadId));
  }

  getResourceType(): Observable<string> {
    return this.store.select(selectResourceType);
  }

  closeCreation(): void {
    this.store.dispatch(CloseThreadCreationAction());
  }

  open(): void {
    this.store.dispatch(OpenAction());
  }

  focusComment(resourceType: string, resourceId: string): void {
    this.store.dispatch(FocusResourceAction({ resourceType, resourceId }));
  }

  createThread(resourceType: string, resourceId: string): void {
    this.store.dispatch(CreateThreadForResourceAction({ resourceType, resourceId }));
  }

  editThread(threadId: string): void {
    this.store.dispatch(EditThreadAction({ threadId }));
  }

  editComment(commentId: string): void {
    this.store.dispatch(EditCommentAction({ commentId }));
  }

  replyToThread(threadId: string): void {
    this.store.dispatch(ReplyToThreadAction({ threadId }));
  }

  displayAllThreads(): void {
    this.store.dispatch(DisplayAllThreadsAction());
    this.setQueryParam();
  }

  displaySingleThread(resourceType: string, resourceId: string): void {
    this.store.dispatch(DisplaySingleThreadAction({ resourceId }));
    this.commentingUserEventsService.trackDisplaySingleThreadEvent();
    this.focusingService.focusSingleResource(
      CommentEntityNameEnum.Resource,
      createComposedResourceId(resourceType, resourceId)
    );
    this.setQueryParam(resourceId);
  }

  closeSingleThread(): void {
    this.setQueryParam();
    this.store.dispatch(CloseSingleThreadDisplayAction());
    this.commentingUserEventsService.trackCloseSingleThreadDisplayEvent();
  }

  setFilterOption(selectedFilterOption: CommentFilterOptionDefinition): void {
    return this.store.dispatch(SetFilterOptionAction({ selectedFilterOption }));
  }

  changeSortOption(selectedSortOption: ThreadSort): void {
    this.store.dispatch(ChangeSortingOptionAction({ selectedSortOption }));
  }

  closeReplying(): void {
    this.store.dispatch(CloseReplyToThreadAction());
  }

  closeEditing(): void {
    this.store.dispatch(CloseEditAction());
  }

  close(): void {
    this.store.dispatch(CloseAction());
    this.setQueryParam();
  }

  init(commentingConfig: CommentingConfig): void {
    this.store.dispatch(InitializeCommentingPanelAction({ commentingConfig }));
  }

  addNewResources(resources: CommentingResourceModel[]): void {
    this.store.dispatch(AddNewResourcesAction({ resources }));
  }

  destroy(): void {
    this.store.dispatch(DestroyAction());
  }

  private getThreadViewModelsStream(): Observable<ThreadViewModel[]> {
    return this.store.select(selectThreadViewModels).pipe(filter(Boolean));
  }

  private async setQueryParam(resourceId: string = null): Promise<void> {
    this.router.navigate([], {
      queryParams: {
        ...this.router.routerState.snapshot.root.queryParams,
        [selectedSingleThreadControlNameQueryParam]: resourceId,
      },
      replaceUrl: true
    });
  }
}
